// Name analysis and standardization utilities
import * as XLSX from "xlsx";

/**
 * Analyze teacher names for variations and suggest standardizations
 * @param {Object} excelData - Excel data object with data array
 * @returns {Object} Analysis results with variations and statistics
 */
export const analyzeNameVariations = (excelData) => {
  // More extensive logging for debugging
  console.log(
    "analyzeNameVariations called with data object:",
    excelData ? `${excelData.data?.length || 0} rows` : "no data"
  );

  // Guard against invalid input
  if (!excelData || !Array.isArray(excelData.data) || excelData.data.length === 0) {
    console.error("analyzeNameVariations received invalid data:", excelData);
    return {
      variations: {},
      statistics: { totalNames: 0, totalVariations: 0, namesWithVariations: 0 },
    };
  }

  const data = excelData.data;

  // Detect the teacher name field by examining the first few rows
  const firstRow = data[0] || {};
  console.log("First data row:", firstRow);

  // List of possible teacher field names (in order of priority)
  const possibleFieldNames = [
    "tutor_name",
    "tutorName",
    "Teacher",
    "teacher",
    "Tutor",
    "tutor",
    "TeacherName",
    "instructor",
    "professor",
    "Name",
    "name",
    "staff",
    "Staff",
    "faculty",
    "Faculty",
  ];

  // Debug: Show all available field names in the first row
  console.log("🔍 Available fields in first row:", Object.keys(firstRow));

  // Find the first field name that exists in our data
  const teacherFieldName =
    possibleFieldNames.find((field) => {
      const fieldExists = data.some(
        (row) => row[field] !== undefined && row[field] !== null && row[field] !== ""
      );
      if (fieldExists) {
        console.log(`✅ Found teacher field: "${field}"`);
        return true;
      }
      return false;
    }) || "tutor_name"; // Default fallback

  console.log(`🔍 Using field name "${teacherFieldName}" for teacher names`);

  // Sample some rows to see what we're working with
  const sampleRows = data.slice(0, 10).map((row, index) => ({
    index,
    hasTeacherField: row[teacherFieldName] !== undefined,
    teacherValue: row[teacherFieldName],
    allFields: Object.keys(row).slice(0, 5), // Show first 5 field names
  }));

  console.log("Sample rows for debugging:", sampleRows);

  // Create objects to track variations and name frequencies
  const variations = {};
  const nameCounts = {};
  let processedNames = 0;

  // Process all data rows
  data.forEach((row, index) => {
    // Skip empty rows, section headers, or rows without teacher names
    if (!row || !row[teacherFieldName] || row.is_section_header) {
      return;
    }

    // CRITICAL: Preserve original name EXACTLY as it appears - DO NOT TRIM YET!
    const originalNameRaw = String(row[teacherFieldName]);

    // Skip truly empty strings (but preserve names with just spaces)
    // ENHANCED: Also skip names that are only whitespace/empty after string conversion
    if (!originalNameRaw || originalNameRaw.trim() === "") {
      console.log(`⏭️ Skipping empty/whitespace-only name: "${originalNameRaw}"`);
      return;
    }

    // Debug log to see exactly what we're getting from Excel
    if (originalNameRaw.includes("Tariq") || originalNameRaw.includes("Sek")) {
      console.log(`🔍 RAW NAME DEBUG: "${originalNameRaw}"`);
      console.log(
        `   Character codes:`,
        originalNameRaw.split("").map((c) => `${c}(${c.charCodeAt(0)})`)
      );
      console.log(`   Has leading space:`, originalNameRaw !== originalNameRaw.trim());
      console.log(`   Trimmed version:`, originalNameRaw.trim());
    }

    processedNames++;

    // Create a normalized key for grouping similar names
    // ENHANCED LOGIC: Handle cases where names might be written with/without spaces
    // CRITICAL UPDATE: Extensively handle leading space variations (common Excel issue)
    let normalizedKey = originalNameRaw
      .toLowerCase()
      .replace(/^(mr\.?|mrs\.?|ms\.?|dr\.?|prof\.?)\s*/i, "") // Remove common prefixes
      .replace(/[.\u00B7\u2022\u2219\u22C5]/g, "") // Remove dots and similar characters
      .replace(/^[\s\u00A0\u2000-\u200A\u202F\u205F\u3000]+/g, "") // CRITICAL: Remove ALL leading spaces/non-breaking spaces
      .replace(/[\s\u00A0\u2000-\u200A\u202F\u205F\u3000]+/g, " ") // Normalize all types of spaces to single spaces
      .trim();

    // CRITICAL: Skip names that result in empty normalized keys (this was causing ghost entries!)
    if (!normalizedKey || normalizedKey.length === 0) {
      console.log(`⏭️ Skipping name with empty normalized key: "${originalNameRaw}"`);
      return;
    }

    // ENHANCED LEADING SPACE DETECTION: Create additional variants to catch common patterns
    // This addresses the user's issue with " M Tariq", "  M Tariq", etc.
    // BUT ONLY add variants that actually exist in the data - no artificial ones!

    // If original name had leading spaces, log it for debugging
    if (originalNameRaw !== originalNameRaw.trim()) {
      console.log(`🔍 LEADING SPACE DETECTED: "${originalNameRaw}" (actual data)`);
    }

    // REMOVED: Artificial variant generation that was creating ghost entries
    // The normalization key already handles grouping, we just need to preserve actual names

    // SMART GROUPING: If name has no spaces but starts with a single letter,
    // try to intelligently add a space after the first letter
    if (
      !normalizedKey.includes(" ") &&
      normalizedKey.length > 1 &&
      /^[a-z][a-z]/.test(normalizedKey)
    ) {
      // Check if it might be a pattern like "mtariq" that should be "m tariq"
      const possibleSplit = normalizedKey.charAt(0) + " " + normalizedKey.slice(1);
      console.log(`🤔 Considering smart split: "${normalizedKey}" → "${possibleSplit}"`);
      normalizedKey = possibleSplit;
    }

    // Enhanced debugging - show exact character codes for problematic names
    if (
      originalNameRaw.includes("Tariq") ||
      originalNameRaw.includes("tariq") ||
      originalNameRaw.includes("Sek") ||
      originalNameRaw.includes("sek")
    ) {
      console.log(`🔍 DEBUG: "${originalNameRaw}" → "${normalizedKey}"`);
      console.log(
        `   Original chars:`,
        originalNameRaw.split("").map((c) => `${c}(${c.charCodeAt(0)})`)
      );
      console.log(
        `   Normalized chars:`,
        normalizedKey.split("").map((c) => `${c}(${c.charCodeAt(0)})`)
      );
    }

    // Initialize the set of variations if this is a new normalized name
    if (!variations[normalizedKey]) {
      variations[normalizedKey] = new Set();
    }

    // Add this specific variation to our set - PRESERVE ORIGINAL RAW NAME
    variations[normalizedKey].add(originalNameRaw);

    // Track how many times each specific name appears
    nameCounts[originalNameRaw] = (nameCounts[originalNameRaw] || 0) + 1;

    // Enhanced logging for debugging (show all names that contain periods or specific patterns)
    if (
      index < 15 ||
      originalNameRaw.includes(".") ||
      originalNameRaw.includes("Tariq") ||
      originalNameRaw.includes("Mughal") ||
      originalNameRaw.includes("Jim") ||
      originalNameRaw.includes("JERRY") ||
      originalNameRaw.includes("Sek")
    ) {
      console.log(`🔍 Processing name: "${originalNameRaw}" → normalized as: "${normalizedKey}"`);
    }
  });

  // ENHANCEMENT: Add potential standardization suggestions for names with problematic patterns
  // This will suggest standardization even for names that don't have actual variations in the data
  const potentialStandardizations = {};

  Object.entries(variations).forEach(([normalizedKey, nameSet]) => {
    const names = Array.from(nameSet);

    // CRITICAL: Skip groups where all names are effectively empty
    const hasValidNames = names.some((name) => name && name.trim().length > 0);
    if (!hasValidNames) {
      console.log(`⏭️ Skipping empty name group: "${normalizedKey}"`);
      return;
    }

    // Check if any name in this group has patterns that suggest standardization
    const hasProblematicPatterns = names.some(
      (name) =>
        name.includes(".") || // Has dots
        /\s{2,}/.test(name) || // Has multiple spaces
        /^(mr\.?|mrs\.?|ms\.?|dr\.?|prof\.?)\s*/i.test(name) || // Has prefixes
        name !== name.trim() || // Has leading/trailing spaces (CRITICAL for user's issue)
        /^[\s\u00A0\u2000-\u200A\u202F\u205F\u3000]/.test(name) // Has any type of leading space character
    );

    if (hasProblematicPatterns) {
      // Add to potential standardizations even if only one variant exists
      potentialStandardizations[normalizedKey] = nameSet;
      console.log(
        `🎯 Found name needing standardization: "${normalizedKey}" with variants:`,
        names
      );
    }
  });

  console.log(`✅ Processed ${processedNames} teacher names`);
  console.log(`📊 Found ${Object.keys(variations).length} unique normalized names`);
  console.log(
    `🎯 Found ${Object.keys(potentialStandardizations).length} names needing standardization`
  );

  // Calculate statistics based on potential standardizations (not just actual variations)
  let totalVariations = 0;
  let namesWithVariations = 0;

  // Count groups that have actual variations OR problematic patterns
  Object.entries(variations).forEach(([key, nameSet]) => {
    if (nameSet.size > 1 || potentialStandardizations[key]) {
      namesWithVariations++;
      totalVariations += nameSet.size;
    }
  });

  // Final statistics object
  const statistics = {
    totalNames: Object.keys(nameCounts).length,
    totalVariations,
    namesWithVariations,
    teacherFieldName, // Save the field name for future reference
  };

  console.log("📊 Name analysis statistics:", statistics);

  // Use potential standardizations instead of just filtering for multiple variations
  // This will show names that need standardization even if they don't have variations in the data
  const filteredVariations = {};
  Object.entries(variations).forEach(([key, nameSet]) => {
    // CRITICAL: Skip empty normalized keys and empty name sets
    if (!key || key.trim() === "" || !nameSet || nameSet.size === 0) {
      console.log(`⏭️ Skipping invalid variation group: "${key}"`);
      return;
    }

    // CRITICAL: Skip groups where all names are effectively empty
    const names = Array.from(nameSet);
    const hasValidNames = names.some((name) => name && name.trim().length > 0);
    if (!hasValidNames) {
      console.log(`⏭️ Skipping empty name group: "${key}" with names:`, names);
      return;
    }

    // Include if: has multiple variations OR has problematic patterns needing standardization
    if (nameSet.size > 1 || potentialStandardizations[key]) {
      filteredVariations[key] = nameSet;
      console.log(`📝 Including for standardization "${key}":`, Array.from(nameSet));
    }
  });

  // Enhanced debugging - show all detected names and their groups
  console.log(`🎯 Total unique names found: ${Object.keys(nameCounts).length}`);
  console.log(`🔗 Groups with variations: ${Object.keys(filteredVariations).length}`);

  // Show some specific examples if they exist
  Object.entries(filteredVariations).forEach(([key, nameSet]) => {
    const variations = Array.from(nameSet);
    if (
      variations.some(
        (name) => name.includes("Tariq") || name.includes("Mughal") || name.includes(".")
      )
    ) {
      console.log(`🎯 EXAMPLE GROUP "${key}":`, variations);
    }
  });

  return {
    variations: filteredVariations,
    statistics,
  };
};

/**
 * Standardize teacher names in Excel data based on user selections
 * @param {Object} excelData - Original Excel data object with data array
 * @param {Object} selectedNames - User's name standardization choices
 * @returns {Object} Updated Excel data with standardized names
 */
export const standardizeNames = (excelData, selectedNames) => {
  if (!excelData || !Array.isArray(excelData.data) || !selectedNames) {
    console.error("Invalid data provided to standardizeNames:", {
      hasExcelData: !!excelData,
      hasDataArray: !!excelData?.data,
      selectedNames,
    });
    return excelData;
  }

  // For debugging - log what we received
  console.log("🔄 Standardizing names with selections:", selectedNames);

  // Analyze the data to get the field name and variations
  const analysis = analyzeNameVariations(excelData);
  const teacherFieldName = analysis.statistics.teacherFieldName;

  console.log(`🏷️ Using teacher field name: "${teacherFieldName}" for standardization`);

  // Create a direct mapping of all name variations to their selected standard
  const nameMapping = {};

  // For each normalized key, map all its variations to the selected standard name
  Object.entries(selectedNames).forEach(([normalizedKey, selectedStandard]) => {
    const variations = analysis.variations[normalizedKey];

    if (variations) {
      // Map each original variation to the selected standard name
      Array.from(variations).forEach((variant) => {
        nameMapping[variant] = selectedStandard;
        console.log(`📝 Mapping "${variant}" → "${selectedStandard}"`);
      });
    } else {
      console.log(`⚠️ Warning: Could not find variations for "${normalizedKey}"`);
    }
  });

  // Create a deep copy of the data to avoid mutating the original
  const standardizedData = JSON.parse(JSON.stringify(excelData));

  // Replace names in the data with standardized versions
  let replacementCount = 0;
  standardizedData.data = excelData.data.map((row) => {
    if (row && row[teacherFieldName] && nameMapping[row[teacherFieldName]]) {
      replacementCount++;
      return { ...row, [teacherFieldName]: nameMapping[row[teacherFieldName]] };
    }
    return row;
  });

  console.log(`✅ Names standardized: ${replacementCount} replacements made`);
  return standardizedData;
};

/**
 * Export standardized Excel data to a file
 * @param {Object} excelData - The Excel data object with standardized names
 * @param {string} fileName - Name for the exported file
 * @returns {Object} Result of the export operation
 */
export const exportStandardizedExcel = (excelData, fileName) => {
  try {
    if (!excelData || !excelData.data || !Array.isArray(excelData.data)) {
      console.error("No data available to export", excelData);
      return { success: false, message: "No data available to export" };
    }

    console.log(`📤 Exporting standardized Excel data with ${excelData.data.length} rows`);

    // Create a new workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData.data);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Standardized Names");

    // Generate a filename
    const baseName = fileName ? fileName.split(".")[0] : "export";
    const exportFileName = `${baseName}_standardized.xlsx`;

    // Write the workbook and save as a file
    XLSX.writeFile(wb, exportFileName);

    console.log(`✅ Successfully exported to "${exportFileName}"`);
    return {
      success: true,
      message: `Excel file with standardized names exported successfully as "${exportFileName}"`,
    };
  } catch (error) {
    console.error("❌ Error exporting standardized Excel:", error);
    return {
      success: false,
      message: `Failed to export Excel: ${error.message}`,
    };
  }
};
